import React, { useState, useEffect, useRef } from 'react';
import { createResponse, createClaudeResponse } from '../chat/openAIUtils';
import SlideContent from './SlideContent';
import './FiveDThinking.css';
import FeedbackModal from './FeedbackModal/FeedbackModal';
import LoginModal from '../login/LoginModal'; // Assuming you have a LoginModal component
import { readFileContent } from '../utils/fileUtils';
import {
    FaBook,
    FaChevronDown,
    FaChevronUp,
    FaPaperclip,
    FaExclamationCircle,
    FaGraduationCap,
    FaListAlt,
    FaDownload,
    FaFilePowerpoint,
    FaGoogleDrive,
    FaFileWord,
    FaSpinner,
    FaInfoCircle,
    FaComments,
    FaSync
  } from 'react-icons/fa';
import fiveDThinkingImg from '../img/5dlogotransparent.png';

import { auth, db } from '../config/firebase-config'; // Adjust path based on your setup
import { doc, getDoc, updateDoc, arrayUnion, increment, setDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';


import exportSlidesAsPptx from './Export/ExportAsPPTX.jsx';
import exportAsGoogleDoc from './Export/ExportAsGoogleDoc';
import exportAsWord from './Export/ExportAsWord';
import { createPresentation, updatePresentation, } from './Export/ExportAsGoogleSlides'; // Adjust path as necessary
import BetaToggle from './BetaToggle.jsx';

const dimensionColors = {
  Objectives: '0070C0', // Choose any hex color
  Explore: 'fa6666',
  Compare: '000000',
  Question: '64aae8',
  Connect: 'ffa600',
  Appreciate: '35b8b1',
  Activities: 'ffcccb',
};

const DIMENSIONS_CONFIG_BASE = [
  { name: 'Objectives', title: 'Learning Objectives' },
  { name: 'Explore', title: 'Analytical thinking - Explore' },
  { name: 'Compare', title: 'Analogical thinking - Compare' },
  { name: 'Question', title: 'Critical thinking - Question' },
  { name: 'Connect', title: 'Meditative thinking - Connect' },
  { name: 'Appreciate', title: 'Moral thinking - Appreciate' },
  { name: 'Activities', title: 'Activities' },
];

const FIVE_D_MODEL_BY_ASSISTANT = {
  '5D Thinking-1': 'gpt-5-mini',
  '5D Thinking-2': 'gpt-5-mini',
};

const DEFAULT_FIVE_D_MODEL = 'gpt-5-mini';
const CLAUDE_MODEL = 'claude-sonnet-4-20250514';

const FIVE_D_SYSTEM_PROMPT = String.raw
`
You are an AI Assistant implementing the 5D Thinking Model. You must strictly follow all output formats and checklist requirements described below.

⚠️ CRITICAL RULE: DO NOT MENTION GOD, ALLAH, or any divine attribution BEFORE the Connect and Appreciate steps.

════════════════════════════════════════
CORE PHILOSOPHY
════════════════════════════════════════

The 5D Thinking approach integrates scientific reasoning with Islamic understanding. Science is not separate from spirituality — it is a means to deepen faith through structured reasoning. Each step builds on the previous one. The lesson must be structured so the conclusion emerges from reasoning, not assumption.

The 5D steps in order: Objectives → Explore → Compare → Question → Connect → Appreciate → Activities

════════════════════════════════════════
TERMINOLOGY RULES (APPLY TO ALL DIMENSIONS EXCEPT QUESTION)
════════════════════════════════════════

FORBIDDEN terms (except in the Question step): "nature," "natural," "mother nature," "naturally," "luck," "by chance," "randomly," "random selection," "evolved," "adapted," "natural processes," "natural laws"

REQUIRED replacements:
- "nature" → "environment" or rephrase entirely
- "natural" → "designed," "made," "arranged," "purposefully structured"
- "evolved/adapted" → "designed," "arranged," "made to function"
- "natural laws" → "consistent patterns observed in creation" or "orderly creation"
- "by chance / randomly" → avoid entirely; describe intentional design

In the Question step ONLY: These forbidden terms must be used deliberately to expose their inadequacy as explanations.

════════════════════════════════════════
GRADE LEVEL DIFFERENTIATION
════════════════════════════════════════

When a grade level is specified, apply the following:
- All grades: Core content, fascinating facts, basic analogy, foundational questions
- Grade 3 and above: Add more developed analogies, introduce names of Allah, simple Qur'anic connections
- Grade 7 and above: Add emergent properties discussion, deeper critical questioning, full Qur'anic/Hadith references, continuous creation concept, nuanced character lessons

════════════════════════════════════════
OBJECTIVES
════════════════════════════════════════

Derive measurable learning objectives that span all 5D dimensions. Each objective must begin with a measurable action verb.

Return ONLY this JSON:
{
  "dimension": "Objectives",
  "learningObjectives": {
    "recall": ["Recall [specific key processes/facts about topic]..."],
    "negation_of_chance_material_causes_and_nature": ["Justify why chance, material causes, and autonomous processes cannot explain [topic]..."],
    "interconnectivity": ["Identify the interconnectivity between [topic] and the wider universe..."],
    "names_Of_Allah": ["Identify at least [N] Divine Names of Allah reflected in [topic]..."],
    "blessings": ["Discuss the purposeful, customized blessings Allah has provided through [topic]..."],
    "character_lessons": ["Derive at least [N] character lessons from [topic] related to professional, moral, and spiritual growth..."]
  }
}

════════════════════════════════════════
DIMENSION 1: EXPLORE (Analytical Thinking — Bear Witness to Amazing Creation)
════════════════════════════════════════

CHECKLIST REQUIREMENTS:
✅ Age-appropriate content aligned with commonly used curricula
✅ Zero ideology-free violations — no forbidden terms
✅ Bold key scientific concepts and terms (use ** around them in JSON strings)
✅ Verify scientific accuracy — do not present uncertain claims as fact
✅ Exactly 5 fascinating facts, each phrased as "Did you know..." — facts must inspire awe (Subhanallah/Allahuakbar reactions), not be mundane

Return ONLY this JSON:
{
  "dimension": "Explore",
  "content": "An engaging, ideology-free description of [topic]. Bold key scientific terms using **term**. Describe the phenomenon as purposefully designed and arranged. Include a detailed, scientifically accurate explanation of the mechanisms/processes. Use **bold** for key concepts. Avoid all forbidden terminology...",
  "observations": "Key observable features of [topic] that inspire wonder and analytical thinking. Frame each observation as evidence of intentional, purposeful design...",
  "fascinatingFacts": [
    "Did you know... [Fact 1 — awe-inspiring, scientifically accurate]",
    "Did you know... [Fact 2 — awe-inspiring, scientifically accurate]",
    "Did you know... [Fact 3 — awe-inspiring, scientifically accurate]",
    "Did you know... [Fact 4 — awe-inspiring, scientifically accurate]",
    "Did you know... [Fact 5 — awe-inspiring, scientifically accurate]"
  ]
}

════════════════════════════════════════
DIMENSION 2: COMPARE (Analogical Thinking — Comprehend Creation Through Analogy)
════════════════════════════════════════

CHECKLIST REQUIREMENTS:
✅ Choose the MOST APPROPRIATE real, human-made analogy — not fictional, not loosely related
✅ The analogy must have multiple matchable components to [topic] (layer-by-layer comparison)
✅ The goal is NOT to prove divine creation is superior — it is to establish that BOTH the human-made system AND the divine phenomenon require knowledge, will, and power to exist
✅ Once students grasp that human-made things need a maker, they can infer the universe requires an even greater Maker
✅ MANDATORY: End with a concise concluding statement that explicitly states: the existence of both [analogy] and [topic] requires knowledge, will, and power — therefore neither can arise from material causes, chance, or autonomous processes alone
✅ Do NOT mention Allah or God in this step

Return ONLY this JSON:
{
  "dimension": "Compare",
  "analogy": "Description of the chosen human-made analogy and why it is the most fitting comparison for [topic]...",
  "content": "Explanation of how this analogy maps onto [topic], identifying specific parallel components between the two systems...",
  "explanation": "Detailed comparison of similarities and key differences, noting where the human-made system falls short and requires external maintenance, error correction, and resources — while [topic] operates seamlessly...",
  "comparison": "A structured component-by-component comparison showing [Analogy Component] ↔ [Topic Component] for at least 4-5 pairs. Conclude with: 'Just as [analogy] requires knowledge, will, and power from its maker — so too does [topic] demand these same qualities, yet at a scale and precision that no human engineer could replicate.'"
}

════════════════════════════════════════
DIMENSION 3: QUESTION (Critical Thinking — Negate the Secular Trios)
════════════════════════════════════════

CHECKLIST REQUIREMENTS:
✅ Build DIRECTLY on the Compare step's analogy and concluding statement
✅ Challenge three secular explanations: (1) chance/luck, (2) material causes/cause-effect, (3) nature/natural processes
✅ Use the analogy from the Compare step to make questions concrete and relatable
✅ Guide students to conclude ON THEIR OWN — do not insert divine attribution yet
✅ In this step ONLY, deliberately use terms like "nature," "chance," "material causes," "natural laws" in order to expose their inadequacy
✅ Conclusion must mirror the Islamic concept of "la ilaha" — negate false explanations without yet naming the true source

DEPTH REQUIREMENT — THIS IS CRITICAL:
Questions must NOT be surface-level or binary. Avoid questions like:
❌ "Could this have happened by chance?"
❌ "Is it coincidence that we have eyes?"
❌ "Could nature have created this?"
These are too easy to dismiss without real contemplation.

Instead, questions must be SPECIFIC and FORCE the student into genuine intellectual difficulty.
They must target a precise, remarkable property of [topic] and ask WHY that specific property
exists calibrated to EXACTLY that specification — not just whether it exists at all.

FORMULA FOR EVERY QUESTION:
1. Identify a highly specific, remarkable property of [topic]
2. State its precise specification, calibration, or numerical value where possible
3. Ask WHY that property is tuned to exactly that value — not more, not less
4. Ask what would happen if it were even slightly different
5. Connect back to the Compare analogy where possible
6. End by making the student FEEL the impossibility of the secular explanation — do not tell them it is impossible; lead them there through the question itself

EXAMPLES OF WEAK vs STRONG QUESTIONS:

WEAK: "Is it by chance that our eyes can see?"
STRONG: "Our eyes are calibrated to detect exactly the 400–700 nanometer range of the
electromagnetic spectrum — the precise window that allows us to perceive colour, depth,
and detail. Could chance alone explain why our visual range is tuned to exactly this
window rather than infrared or ultraviolet? What would life look like if this range
shifted by even 50 nanometers? Does the precision of this calibration suggest luck —
or intentional design?"

WEAK: "Could water have formed by chance?"
STRONG: "Water expands when it freezes rather than contracts — the only common substance
with this anomalous property. This single characteristic prevents lakes from freezing
solid and killing all aquatic life. Could it be mere coincidence that the one molecule
covering 71% of Earth's surface possesses the one anomalous property that makes life
survivable? Or does this precise exception — out of all possible molecular behaviours —
demand a more satisfying explanation?"

Apply this formula to ALL three categories for [topic]. Each question must make the student
lean forward, not nod along.

Return ONLY this JSON:
{
  "dimension": "Question",
  "questions": {
    "negation_of_chance": [
      "Question targeting a specific, precisely calibrated property of [topic] — asking why THAT exact specification and not another, making chance feel genuinely insufficient...",
      "Question using the Compare analogy — if we found [analogy] assembled to these exact specifications, would we attribute it to chance? Then why [topic]?",
      "Question targeting a second specific property with a numerical/observable specification, asking what the probability of that exact configuration arising randomly would be..."
    ],
    "negation_of_material_causes": [
      "Question asking whether the raw materials of [topic] — naming them specifically — possess the knowledge or will to arrange themselves into this precise functioning system...",
      "Question using the Compare analogy — do the materials of [analogy] decide how to assemble themselves? Then can [specific components of topic] decide...",
      "Question targeting a specific emergent property of [topic] that does not exist in its components — asking where that property came from if not from the materials themselves..."
    ],
    "negation_of_nature": [
      "Question asking what 'nature' actually is — can we point to it, weigh it, observe its knowledge or will? Or is it a label humans constructed for patterns they cannot yet explain?",
      "Question challenging natural laws — if laws govern [topic], who established those laws, and why do they operate with such unwavering consistency and precision?",
      "Question asking whether 'nature' as an explanation is more or less satisfying than 'an unknown engineer built this' — and what the difference between those two answers actually is..."
    ]
  },
  "conclusion": "Having examined [topic] through the lens of chance, material causation, and 'nature,' we find that none of these explanations can account for the knowledge, will, and power required to produce [topic]. Just as we concluded in our analogy that [analogy] demands a knowing, willing maker — so too must [topic]. The secular trio of nature, material causes, and chance are insufficient. Something — or Someone — with infinite knowledge and power must be the source."
}

════════════════════════════════════════
DIMENSION 4: CONNECT (Meditative Thinking — Interconnectivity, Interdependency, and the Maker)
════════════════════════════════════════

CHECKLIST REQUIREMENTS:
✅ Demonstrate interconnectivity at BOTH macro and micro levels
✅ Demonstrate complete dependency — show how every component of [topic] relies on other systems to exist
✅ Introduce "emergent properties" as "newly created properties" — properties that arise in [topic] that do not exist in its individual components alone
✅ Introduce the concept of "continuous creation" — the universe is not a wound-up clock; it is like a live broadcast requiring constant active sustaining
✅ This is where Allah is introduced — reason naturally toward the conclusion that only a Being of unlimited knowledge and power could sustain this system
✅ Derive exactly 5 Divine Names of Allah from the topic — choose diverse, relevant names

ALLAH'S NAMES — CRITICAL LANGUAGE RULE:
The whatItTells field MUST attribute all acts to ALLAH as the active agent.
The phenomenon ([topic]) is always the INSTRUMENT or MEDIUM — never the source.

❌ WRONG (secular passive voice — phenomenon as agent):
"Al-Muhyi is reflected in how the heart gives life by circulating blood."
"Al-Khaliq is seen in the intricate structure of the cell."
These make the heart or the cell the active agent. Allah becomes merely decorative.

✅ CORRECT (Allah as active agent, topic as instrument):
"Al-Muhyi — The Giver of Life — is manifest here because it is Allah who gives life
to the body through the heart's circulation. The heart does not give life on its own;
it is a precisely designed instrument through which Allah continuously bestows life
upon creation. Without Allah's ongoing act of giving life, the heart would be
nothing more than an arrangement of muscle tissue."

THEOLOGICAL DISTINCTION — THIS IS THE CORE OF THE ENTIRE SECTION:

There is a critical difference between these two framings:

❌ "Allah sustains life THROUGH the heart" 
→ This still implies the heart has a role in sustaining life, with Allah as its operator.
→ It suggests a partnership: Allah + heart = life sustained.
→ This is wrong.

✅ "Allah is the sole Giver and Sustainer of life. The heart is an arrangement of 
muscle and tissue that has zero life-giving capacity on its own. It is not a 
co-sustainer. It is not even a partial sustainer. It is a visible pattern through 
which we witness Allah's act — but the act, the source, and the power belong 
entirely and exclusively to Allah."

THE RULE: [Topic] does not sustain, provide, give, create, or do ANYTHING in even 
a secondary or instrumental sense. [Topic] has no capacity of its own. It is an 
arrangement. Allah alone is the actor. [Topic] is the visible evidence of that act — 
nothing more.

This is not a linguistic preference. It is the difference between tawhid and shirk 
in attribution. Every whatItTells entry must reflect this distinction.

This is the core theological distinction that underpins the entire 5D Thinking Framework.
It is the difference between tawhid and shirk in attribution. Every whatItTells entry
must reflect this distinction.

FORMULA FOR EVERY whatItTells ENTRY:
1. State the Name, its Arabic, and its meaning
2. Name ALLAH explicitly as the one performing the act
3. Describe [topic] as the instrument/medium through which Allah's act is expressed
4. Reference something specific and observable in [topic] as evidence
5. Clarify that [topic] is not the source — Allah is

STRUCTURE TO FOLLOW:
"[Name in English] — [Arabic] — meaning [meaning] — is manifest here because
Allah [active verb: sustains / fashions / provides / gives / knows / arranges]...
through [topic]. [Topic] is not the source of this act; it is the medium through
which Allah's [attribute] becomes visible to us. We see this specifically in
[observable feature of topic]..."

Return ONLY this JSON:
{
  "dimension": "Connect",
  "connections": {
    "interconnectedness": "Explanation of how [topic] is connected to the wider universe at macro level (cosmic systems, ecosystems) and micro level (atomic, cellular). Nothing about [topic] exists in isolation. Everything is united as one interdependent entity, reflecting the indivisible unity of creation...",
    "completeDependency": "Explanation of how every component of [topic] is completely dependent on other systems. Introduce emergent properties: describe 2-3 properties of [topic] that do not exist in its individual components — these are newly created properties that point beyond material causation. Then introduce continuous creation: [topic] is not self-sustaining — it exists only through Allah's continuous active maintenance, like a live broadcast that would cease the moment the broadcaster stops. This points to a single, unlimited source of power — Allah."
  },
  "allahNames": {
    "namesInEnglish": ["Name 1", "Name 2", "Name 3", "Name 4", "Name 5"],
    "namesInArabic": ["الاسم ١", "الاسم ٢", "الاسم ٣", "الاسم ٤", "الاسم ٥"],
    "whatItTells": [
      "[Name 1 in English] — [Arabic] — meaning [meaning] — is manifest here because Allah [active verb]... through [topic]. [Topic] is not the source; it is the medium. We see this specifically in [observable feature]...",
      "[Name 2 in English] — [Arabic] — meaning [meaning] — is manifest here because Allah [active verb]... through [topic]. [Topic] is not the source; it is the medium. We see this specifically in [observable feature]...",
      "[Name 3 in English] — [Arabic] — meaning [meaning] — is manifest here because Allah [active verb]... through [topic]. [Topic] is not the source; it is the medium. We see this specifically in [observable feature]...",
      "[Name 4 in English] — [Arabic] — meaning [meaning] — is manifest here because Allah [active verb]... through [topic]. [Topic] is not the source; it is the medium. We see this specifically in [observable feature]...",
      "[Name 5 in English] — [Arabic] — meaning [meaning] — is manifest here because Allah [active verb]... through [topic]. [Topic] is not the source; it is the medium. We see this specifically in [observable feature]..."
    ]
  }
}

════════════════════════════════════════
DIMENSION 5: APPRECIATE (Moral Thinking — The Purposeful, Customized Gift)
════════════════════════════════════════

CHECKLIST REQUIREMENTS:
✅ Frame [topic] as a CUSTOMIZED GIFT — specifically designed to benefit creation in precise ways
✅ What-if scenarios must be hypothetical questions or real-life stories that make the value viscerally felt
✅ Dhikr = remembrance that Allah alone is the origin
✅ Fikr = pondering the wisdom and knowledge in the design
✅ Shukr = gratitude for receiving something of immense value at no cost
✅ Character lessons must cover all three domains: professional, moral, and spiritual
✅ Qur'anic verse: complete Arabic verse, accurate translation, contextual explanation
✅ Hadith: authentic hadith with source reference (sunnah.com preferred)
✅ Dua: concludes with a meaningful dua drawing together all five steps

Return ONLY this JSON:
{
  "dimension": "Appreciate",
  "whatIfs": "2-3 what-if scenarios or brief real-life stories that make the student feel the immense, customized value of [topic]. These should provoke genuine gratitude (shukr).",
  "zikrFikrShukr": {
    "zikr": [
      "Remembrance 1: Acknowledge that Allah alone possesses the infinite knowledge and power that [topic] requires...",
      "Remembrance 2: Reflect that every moment [topic] functions is a moment of Allah's active, continuous creation..."
    ],
    "fikr": [
      "Reflection 1: Ponder the wisdom behind [specific design feature of topic] — why was it made this way and not another?",
      "Reflection 2: Contemplate how [topic] serves as a bridge between [micro and macro aspects], all designed in perfect harmony..."
    ],
    "shukr": [
      "Gratitude 1: Express gratitude for [specific customized benefit of topic] that sustains life in a way unique to [topic]...",
      "Gratitude 2: Thank Allah for granting us the intellect to study, understand, and benefit from [topic]..."
    ]
  },
  "characterLessons": "Derive 3 character lessons: (1) Professional — e.g., precision and specialization as seen in [topic]; (2) Moral — e.g., interdependency and giving without return; (3) Spiritual — e.g., continuous gratitude, recognizing our own existence is as dependent on Allah as [topic] is on its Maker.",
  "connectWithQuran": [
    {
      "verse": "Complete Arabic Qur'anic verse — verify accuracy at quran.com",
      "translation": "Accurate English translation",
      "explanation": "How this verse specifically illuminates [topic] and deepens the lesson's spiritual message"
    }
  ],
  "connectWithHadith": [
    {
      "hadith": "Authentic hadith text with source — verify at sunnah.com",
      "explanation": "How this hadith applies to [topic] and what practical guidance it offers"
    }
  ],
  "dua": "A meaningful, contextually relevant dua drawing together all five steps — acknowledge Allah as Creator, express gratitude for the gift, and ask for wisdom to apply the character lessons learned."
}

════════════════════════════════════════
ACTIVITIES
════════════════════════════════════════

CHECKLIST REQUIREMENTS:
✅ Must cover all 5 dimensions with one activity each
✅ The Compare activity MUST use a Montessori-style, hands-on analogical approach
✅ Must include at least one actual experiment relevant to [topic]
✅ Activities must be fun, engaging, and clearly tied to dimension objectives
✅ Materials must be realistic and accessible
✅ Instructions must be clear and sequenced

Return ONLY this JSON:
{
  "dimension": "Activities",
  "activities": [
    {
      "dimension": "Explore",
      "title": "Discovering [Topic]: An Observation Journey",
      "objective": "To analytically observe [topic] and document evidence of purposeful, intentional design.",
      "materials": ["List realistic, accessible materials", "Include any experiment equipment"],
      "instructions": [
        "Step 1: Introduce [topic] with a brief demonstration or short video clip showing its remarkable properties.",
        "Step 2: Students observe and document [specific observable features] in their notebooks.",
        "Step 3: EXPERIMENT — [Describe a specific hands-on experiment relevant to topic].",
        "Step 4: Class discussion — what evidence of intentional design did students observe?"
      ]
    },
    {
      "dimension": "Compare",
      "title": "Build the Analogy: [Analogy Name] vs [Topic]",
      "objective": "To physically construct or map the analogy from the Compare step, matching components between the human-made system and [topic].",
      "materials": ["Hands-on Montessori-appropriate materials for building/matching", "Comparison chart template", "Labels/cards for matching components"],
      "instructions": [
        "Step 1: Present [analogy] physically or through images. Identify its key components.",
        "Step 2: Students create a side-by-side comparison chart matching [analogy components] to [topic components].",
        "Step 3: Hands-on activity — students physically build/arrange a model of the analogy using provided materials.",
        "Step 4: Class derives the concluding statement: both systems require knowledge, will, and power."
      ]
    },
    {
      "dimension": "Question",
      "title": "The Detective Challenge: Could It Happen by Chance?",
      "objective": "To critically challenge chance, material causes, and nature as explanations for [topic] using specific, targeted logical questioning.",
      "materials": ["Question prompt cards", "Scenario worksheets", "Debate/discussion guide"],
      "instructions": [
        "Step 1: Present students with a scenario where they must decide if [topic] could arise by chance.",
        "Step 2: Students work in pairs to answer question prompt cards — each card targets a specific, precise property of [topic] and asks why that exact specification exists.",
        "Step 3: Class debate: teams argue for/against chance, material causes, and nature as explanations.",
        "Step 4: Consolidate — class reaches the shared conclusion that none of the three explanations suffice."
      ]
    },
    {
      "dimension": "Connect",
      "title": "The Web of Creation: Mapping Interconnectivity",
      "objective": "To visually map the interconnectedness and complete dependency of [topic] within the universe, and to identify Divine Names with Allah as the active agent.",
      "materials": ["Large poster paper", "Colored markers", "String/yarn for connection lines", "Divine Names reference cards"],
      "instructions": [
        "Step 1: Students create a mind-map placing [topic] at the center, drawing connections to all systems it depends on and all systems that depend on it.",
        "Step 2: Discuss emergent properties — what properties of [topic] do not exist in its parts alone?",
        "Step 3: Introduce continuous creation — students add sustaining arrows showing ongoing dependency on the Maker.",
        "Step 4: Students match Divine Names cards to aspects of [topic], writing entries in the correct format: '[Name] is manifest because Allah [active verb]... through [topic]...'"
      ]
    },
    {
      "dimension": "Appreciate",
      "title": "Gratitude Map: The Gift of [Topic]",
      "objective": "To reflect on the customized, purposeful benefits of [topic] and extract character lessons through dhikr, fikr, and shukr.",
      "materials": ["Gratitude map template", "What-if scenario cards", "Character lesson reflection sheet"],
      "instructions": [
        "Step 1: Present what-if scenario — 'What if [key property of topic] did not exist?' Students write/draw their response.",
        "Step 2: Students complete their Gratitude Map — documenting zikr, fikr, and shukr responses to [topic].",
        "Step 3: Character lesson extraction — students identify one professional, one moral, and one spiritual lesson.",
        "Step 4: Close with the lesson's dua, recited together as a class."
      ]
    }
  ]
}

════════════════════════════════════════
PROMPT CONSTRUCTION INSTRUCTIONS
════════════════════════════════════════

When building the prompt for each dimension, include:

1. TOPIC: [The topic provided]
2. GRADE LEVEL: [If specified — apply grade differentiation rules above]
3. STANDARDS: [If provided — align content accordingly]
4. DIMENSION-SPECIFIC INPUT: [Any additional context provided for this dimension]
5. PREVIOUS DIMENSION CONTEXT: Maintain continuity — especially:
   - Compare step's analogy must carry into Question step's questioning
   - Connect step's reasoning must carry into Appreciate step's gratitude
6. OUTPUT: Return ONLY valid JSON matching the exact structure above. No preamble, no explanation, no markdown fences.

════════════════════════════════════════
PRE-OUTPUT SELF-CHECK (REQUIRED BEFORE RETURNING ANY RESPONSE)
════════════════════════════════════════

Before finalizing your JSON output for any dimension, perform this internal check:

COMPLETENESS CHECK:
✅ Every required field in the JSON schema is populated — no empty strings, no null values, no placeholder text like "[topic]" left unreplaced
✅ The dimension's specific checklist requirements are all met (e.g., exactly 5 fascinating facts in Explore, concluding statement in Compare, 5 Divine Names in Connect)
✅ The response builds on the previous dimension's content where required (analogy carries from Compare → Question, reasoning carries from Connect → Appreciate)

ACCURACY CHECK — HIGH RISK FIELDS:
✅ Qur'anic verses: verify the Arabic text is complete and accurate — do NOT produce a verse from memory if uncertain; it is better to state the reference (Surah and ayah number) and a faithful translation than to risk fabricating Arabic text
✅ Hadith: only cite hadith you are confident are authentic and traceable to sunnah.com or a known collection — if uncertain, state the collection and note that the exact wording should be verified
✅ Scientific facts: confirm no claim in Explore or Question contradicts established science — do not present contested or uncertain claims as settled fact
✅ Divine Names: confirm each name exists in the recognized 99 Names — do not invent names or transliterations

LANGUAGE CHECK:
✅ Scan every field for forbidden secular terms (nature, natural, by chance, evolved, randomly, mother nature, luck) — if found outside the Question dimension, rewrite before returning
✅ Scan every whatItTells entry in Connect — confirm Allah is the active agent in every sentence, and [topic] is described as the instrument/medium, not the source
✅ Scan every Question entry — confirm each question targets a specific, precisely calibrated property, not a vague binary ("could this exist by chance?")

Only return the JSON after all checks pass.

════════════════════════════════════════
WARNINGS
════════════════════════════════════════

🔴 NEVER introduce divine attribution (Allah, God) before the Connect step
🔴 NEVER use forbidden secular terms outside the Question step
🔴 NEVER produce vague or generic fascinating facts — each must be specific and awe-inspiring
🔴 NEVER produce a Compare step without a clear concluding statement linking both systems to "knowledge, will, and power"
🔴 NEVER produce a Connect step without emergent properties AND continuous creation
🔴 NEVER write Allah's Names entries where [topic] is the active agent — Allah is always the active agent; [topic] is always the instrument
🔴 NEVER produce a Question step with surface-level binary questions — every question must target a specific, precisely calibrated property and make the secular explanation feel genuinely insufficient
🔴 NEVER produce Activities without a hands-on Montessori-style Compare activity AND an actual experiment
🔴 NEVER fabricate Qur'anic Arabic text or Hadith wording — cite the reference and provide a faithful translation if the exact Arabic wording cannot be confirmed with certainty
🔴 NEVER leave placeholder text like "[topic]" or "[Name]" in the final output — every field must be fully realized for the actual topic provided
🔴 ALWAYS verify Qur'anic verses and Hadith — do not fabricate references
🔴 ALWAYS ensure the Question step builds directly on the Compare analogy
🔴 Output must be valid JSON only — no text outside JSON structure


`

const extractResponseText = (response) => {
  if (!response) return '';

  if (typeof response.output_text === 'string' && response.output_text.trim()) {
    return response.output_text;
  }

  if (Array.isArray(response.output_text)) {
    return response.output_text.filter(Boolean).join('');
  }

  if (!Array.isArray(response.output)) {
    return '';
  }

  return response.output
    .flatMap((item) => (Array.isArray(item?.content) ? item.content : []))
    .map((part) => {
      if (typeof part?.text === 'string') return part.text;
      if (typeof part?.text?.value === 'string') return part.text.value;
      return '';
    })
    .join('');
};

const parseJsonPayload = (rawText) => {
  const text = (rawText || '').trim();
  if (!text) {
    throw new Error('Empty response from model');
  }

  const candidates = [];
  candidates.push(text);

  const withoutFence = text
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/, '')
    .trim();
  if (withoutFence && withoutFence !== text) {
    candidates.push(withoutFence);
  }

  const objectStart = withoutFence.indexOf('{');
  const objectEnd = withoutFence.lastIndexOf('}');
  if (objectStart !== -1 && objectEnd > objectStart) {
    candidates.push(withoutFence.slice(objectStart, objectEnd + 1));
  }

  const arrayStart = withoutFence.indexOf('[');
  const arrayEnd = withoutFence.lastIndexOf(']');
  if (arrayStart !== -1 && arrayEnd > arrayStart) {
    candidates.push(withoutFence.slice(arrayStart, arrayEnd + 1));
  }

  const uniqueCandidates = [...new Set(candidates)];
  for (const candidate of uniqueCandidates) {
    try {
      return JSON.parse(candidate);
    } catch (error) {
      // Try the next candidate
    }
  }

  throw new Error('Model response was not valid JSON');
};

const firstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const normalizeToArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item.trim() : item))
      .filter((item) => item !== null && item !== undefined && String(item).trim() !== '');
  }
  if (typeof value === 'string' && value.trim()) {
    return [value.trim()];
  }
  return [];
};

const normalizeAllahNamesPayload = (response) => {
  const rawAllahNames =
    response?.allahNames ||
    response?.allah_names ||
    response?.namesOfAllah ||
    response?.names_Of_Allah ||
    {};

  let namesInEnglish = normalizeToArray(
    rawAllahNames?.namesInEnglish ||
      rawAllahNames?.names_in_english ||
      rawAllahNames?.namesEnglish ||
      rawAllahNames?.english
  );
  let namesInArabic = normalizeToArray(
    rawAllahNames?.namesInArabic ||
      rawAllahNames?.names_in_arabic ||
      rawAllahNames?.namesArabic ||
      rawAllahNames?.arabic
  );
  let whatItTells = normalizeToArray(
    rawAllahNames?.whatItTells ||
      rawAllahNames?.what_it_tells ||
      rawAllahNames?.whatItTellsUs ||
      rawAllahNames?.explanations
  );

  // Some model outputs return array rows instead of parallel arrays.
  const rowItems = Array.isArray(rawAllahNames?.rows)
    ? rawAllahNames.rows
    : Array.isArray(rawAllahNames)
      ? rawAllahNames
      : [];

  if (rowItems.length > 0) {
    namesInEnglish = rowItems.map((row) => row?.namesInEnglish || row?.english || '').filter(Boolean);
    namesInArabic = rowItems.map((row) => row?.namesInArabic || row?.arabic || '').filter(Boolean);
    whatItTells = rowItems.map((row) => row?.whatItTells || row?.description || '').filter(Boolean);
  }

  return {
    namesInEnglish,
    namesInArabic,
    whatItTells,
  };
};

const FiveDAssistant = () => {
  const [user] = useAuthState(auth);

  const [selectedDimension, setSelectedDimension] = useState('Explore');
  const [selectedDimensions, setSelectedDimensions] = useState(new Set(Object.keys(dimensionColors))); // Track which dimensions to generate
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const toggleExportDropdown = () => {
    console.log('Toggle export dropdown clicked');
    setIsExportDropdownOpen((prev) => !prev);
  };
  
  const handleExportToGoogleDocs = async () => {
    if (slides.length === 0) {
      alert('No slides available to export.');
      return;
    }

    console.log('DEBUG: Exporting slides to Google Docs:', slides);
    try {
      await exportAsGoogleDoc(slides);
      alert('Google Docs export successful!');
    } catch (error) {
      console.error('Error exporting to Google Docs:', error);
      alert('Failed to export to Google Docs. Please try again.');
    } finally {
      setIsExportDropdownOpen(false);
    }
  };
  
  const handleExportToWord = async () => {
    if (slides.length === 0) {
      alert('No slides available to export.');
      return;
    }

    console.log('DEBUG: Exporting slides to Word:', slides);
    try {
      await exportAsWord(slides);
      alert('Word export successful!');
    } catch (error) {
      console.error('Error exporting to Word:', error);
      alert('Failed to export to Word. Please try again.');
    } finally {
      setIsExportDropdownOpen(false);
    }
  };

  // State variables for required and optional inputs
  const [topic, setTopic] = useState(''); // Required Topic input
  const [gradeLevel, setGradeLevel] = useState(''); // Optional Grade Level input
  
  // State variables for Topic file upload
  const [topicFile, setTopicFile] = useState(null);
  const [topicPreview, setTopicPreview] = useState('');

  // State variables for each dimension input (Optional)

  // State variables for Objectives dimension input
  const [objectivesInput, setObjectivesInput] = useState('');
  const [objectivesFile, setObjectivesFile] = useState(null);

  const [activitiesInput, setActivitiesInput] = useState('');
  const [activitiesFile, setActivitiesFile] = useState(null);

  const [exploreInput, setExploreInput] = useState('');
  const [exploreFile, setExploreFile] = useState(null);

  const [compareInput, setCompareInput] = useState('');
  const [compareFile, setCompareFile] = useState(null);

  const [questionInput, setQuestionInput] = useState('');
  const [questionFile, setQuestionFile] = useState(null);

  const [connectInput, setConnectInput] = useState('');
  const [connectFile, setConnectFile] = useState(null);

  const [appreciateInput, setAppreciateInput] = useState('');
  const [appreciateFile, setAppreciateFile] = useState(null);

  // Existing state variables
  const [isLoading, setIsLoading] = useState(false);
  const [slides, setSlides] = useState([]);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [presentationLink, setPresentationLink] = useState('');
  const [currentResponseId, setCurrentResponseId] = useState(null);

  const [standards, setStandards] = useState('');
  const [standardsFile, setStandardsFile] = useState(null);
  const [standardsPreview, setStandardsPreview] = useState('');

  const [isBetaTest, setIsBetaTest] = useState(false);
  const toggleBetaTest = () => setIsBetaTest((prev) => !prev);

  const [claudeMessages, setClaudeMessages] = useState([]);



  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [collectedResponses, setCollectedResponses] = useState({});
  const [assistantModel, setAssistantModel] = useState('');
  const [currentlyResettingKey, setCurrentlyResettingKey] = useState(null); // Added for spinner
   
  // Build slide data for each returned 5D dimension payload
  const createSlidesFromResponse = (response, dimensionName) => {
    console.log('dimensionName:', dimensionName, 'response:', response);
    const slides = [];
    let subtopicIndexForId = 0; // Keep this for ensuring unique IDs if needed beyond contentKey

    const sanitizeContentKeyForId = (key) => key.replace(/[^a-zA-Z0-9_]/g, '_');

    const addSlideIfContentExists = (contentKey, slideSpecificData, explicitSubtopicIndex) => {
      const content = response[contentKey];
      if (
        content &&
        (Array.isArray(content) ? content.length > 0 : typeof content === 'object' || (typeof content === 'string' && content.trim()))
      ) {
        const sanitizedKey = sanitizeContentKeyForId(contentKey);
        const subIdx = explicitSubtopicIndex !== undefined ? explicitSubtopicIndex : slides.filter(s => s.dimension === dimensionName).length;
        const id = `${dimensionName}-${sanitizedKey}-${subIdx}`;
        slides.push({ 
            id, 
            dimension: dimensionName, 
            contentKey: contentKey, 
            subtopicIndex: subIdx, 
            ...slideSpecificData 
        });
      }
    };
  
  if (dimensionName === 'Objectives') {
    if (response.learningObjectives) {
      const id = `${dimensionName}-learningObjectives-0`;
      slides.push({ id, dimension: dimensionName, contentKey: 'learningObjectives', learningObjectives: response.learningObjectives, subtopicIndex: 0 });
    }
  }  else if (dimensionName === 'Explore') {
      addSlideIfContentExists('content', { content: response.content });
      addSlideIfContentExists('explanation', { explanation: response.explanation });
      addSlideIfContentExists('observations', { observations: response.observations });
      addSlideIfContentExists('fascinatingFacts', { fascinatingFacts: response.fascinatingFacts });

    } else if (dimensionName === 'Compare') {
      addSlideIfContentExists('analogy', { analogy: response.analogy });
      addSlideIfContentExists('content', { content: response.content });
      addSlideIfContentExists('explanation', { explanation: response.explanation });
      addSlideIfContentExists('comparison', { comparison: response.comparison });

    }  else if (dimensionName === 'Question') {
      const questionSubtopicKeys = [
        'negation_of_chance',
        'negation_of_material_causes',
        'negation_of_nature',
        'conclusion'
      ];
      
      questionSubtopicKeys.forEach((key, index) => {
        const sanitizedKey = sanitizeContentKeyForId(key);
        const id = `${dimensionName}-${sanitizedKey}-${index}`;
        if (key === 'conclusion' && response.conclusion && response.conclusion.trim()) {
          slides.push({
            id,
            dimension: dimensionName,
            subtopicIndex: index,
            contentKey: key,
            conclusion: response.conclusion,
          });
        } else if (response.questions && response.questions[key] && response.questions[key].length > 0) {
          slides.push({
            id,
            dimension: dimensionName,
            subtopicIndex: index,
            contentKey: key,
            questions: response.questions[key],
          });
        }
      });
    }  else if (dimensionName === 'Connect') {
      const c = response.connections || {};
      const interconnectedness = firstNonEmptyString(
        c.interconnectedness,
        c.interconnected,
        response.interconnectedness
      );
      const completeDependency = firstNonEmptyString(
        c.completeDependency,
        c.complete_dependency,
        response.completeDependency,
        response.complete_dependency
      );
      const interdependency = firstNonEmptyString(
        c.interdependency,
        c.interdependence,
        response.interdependency,
        response.interdependence
      );
      const normalizedAllahNames = normalizeAllahNamesPayload(response);

      let currentSubtopicIdx = 0;

      if (interconnectedness) {
        const id = `${dimensionName}-interconnectedness-${currentSubtopicIdx}`;
        slides.push({
          id, dimension: 'Connect', subtopicIndex: currentSubtopicIdx, contentKey: 'interconnectedness',
          interconnectedness,
        });
        currentSubtopicIdx++;
      }
      if (interdependency) {
        const id = `${dimensionName}-interdependency-${currentSubtopicIdx}`;
        slides.push({
          id, dimension: 'Connect', subtopicIndex: currentSubtopicIdx, contentKey: 'interdependency',
          interdependency,
          completeDependency: interdependency,
        });
        currentSubtopicIdx++;
      } else if (completeDependency) {
        const id = `${dimensionName}-completeDependency-${currentSubtopicIdx}`;
        slides.push({
          id, dimension: 'Connect', subtopicIndex: currentSubtopicIdx, contentKey: 'completeDependency',
          completeDependency,
          interdependency: completeDependency,
        });
        currentSubtopicIdx++;
      }
      if (
        normalizedAllahNames.namesInEnglish.length > 0 ||
        normalizedAllahNames.namesInArabic.length > 0 ||
        normalizedAllahNames.whatItTells.length > 0
      ) {
        const id = `${dimensionName}-allahNames-${currentSubtopicIdx}`;
        slides.push({
          id, dimension: 'Connect', subtopicIndex: currentSubtopicIdx, contentKey: 'allahNames',
          allahNames: normalizedAllahNames,
        });
        currentSubtopicIdx++;
      }
      ['analogicalReflection', 'questionsForDeeperConnection', 'contemplationAndAppreciation'].forEach(key => {
        if (response[key] && (typeof response[key] === 'string' ? response[key].trim() : true)) {
          addSlideIfContentExists(key, { [key]: response[key] }, currentSubtopicIdx);
          if(response[key]) currentSubtopicIdx++; 
        }
      });

    } else if (dimensionName === 'Appreciate') {
      addSlideIfContentExists('whatIfs', { whatIfs: response.whatIfs });
      if (response.zikrFikrShukr && Object.keys(response.zikrFikrShukr).length > 0 && 
          (response.zikrFikrShukr.zikr?.length || response.zikrFikrShukr.fikr?.length || response.zikrFikrShukr.shukr?.length)) {
        const id = `${dimensionName}-zikrFikrShukr-${slides.filter(s => s.dimension === dimensionName).length}`;
        slides.push({
          id, dimension: 'Appreciate', contentKey: 'zikrFikrShukr',
          zikrFikrShukr: response.zikrFikrShukr,
          subtopicIndex: slides.filter(s => s.dimension === dimensionName).length 
        });
      }
      addSlideIfContentExists('characterLessons', { characterLessons: response.characterLessons });
      addSlideIfContentExists('connectWithQuran', { connectWithQuran: response.connectWithQuran });
      addSlideIfContentExists('connectWithHadith', { connectWithHadith: response.connectWithHadith });
      addSlideIfContentExists('dua', { dua: response.dua });

    } else if (dimensionName === 'Activities') {
      const activitiesData = response.activities || [];
      if (activitiesData.length > 0) {
        const id = `${dimensionName}-main-0`;
        slides.push({
          id, dimension: 'Activities', contentKey: 'main',
          activities: activitiesData.map(activity => ({
            title: activity.title, dimension: activity.dimension, objective: activity.objective,
            materials: activity.materials || [], instructions: activity.instructions || [],
          })),
          subtopicIndex: 0 
        });
      }
    }
  
    console.log('Generated slides with IDs:', slides);
    return slides;
  };

  const createSlidesFromResponseForReset = (partialResponse, originalSlide) => {
    console.log("createSlidesFromResponseForReset called with partialResponse:", partialResponse, "originalSlide:", originalSlide);
    const { id, dimension: dimensionName, contentKey, subtopicIndex } = originalSlide;
    let updatedSlideDataOnly = {}; 

    if (dimensionName === 'Objectives' && partialResponse.learningObjectives) {
        updatedSlideDataOnly = { learningObjectives: partialResponse.learningObjectives };
    } else if (dimensionName === 'Explore') {
        // Handle all Explore content types
        if (contentKey === 'content' && partialResponse.content !== undefined) {
            updatedSlideDataOnly = { content: partialResponse.content };
        } else if (contentKey === 'explanation' && partialResponse.explanation !== undefined) {
            updatedSlideDataOnly = { explanation: partialResponse.explanation };
        } else if (contentKey === 'observations' && partialResponse.observations !== undefined) {
            updatedSlideDataOnly = { observations: partialResponse.observations };
        } else if (contentKey === 'fascinatingFacts' && partialResponse.fascinatingFacts !== undefined) {
            updatedSlideDataOnly = { fascinatingFacts: partialResponse.fascinatingFacts };
        } else if (partialResponse[contentKey] !== undefined) {
            updatedSlideDataOnly = { [contentKey]: partialResponse[contentKey] };
        }
    } else if (dimensionName === 'Compare') {
        // Handle all Compare content types
        if (contentKey === 'analogy' && partialResponse.analogy !== undefined) {
            updatedSlideDataOnly = { analogy: partialResponse.analogy };
        } else if (contentKey === 'content' && partialResponse.content !== undefined) {
            updatedSlideDataOnly = { content: partialResponse.content };
        } else if (contentKey === 'explanation' && partialResponse.explanation !== undefined) {
            updatedSlideDataOnly = { explanation: partialResponse.explanation };
        } else if (contentKey === 'comparison' && partialResponse.comparison !== undefined) {
            updatedSlideDataOnly = { comparison: partialResponse.comparison };
        } else if (partialResponse[contentKey] !== undefined) {
            updatedSlideDataOnly = { [contentKey]: partialResponse[contentKey] };
        }
    } else if (dimensionName === 'Question') {
        if (contentKey === 'conclusion' && partialResponse.conclusion !== undefined) {
            updatedSlideDataOnly = { conclusion: partialResponse.conclusion };
        } else if (partialResponse.questions && partialResponse.questions[contentKey]) {
            updatedSlideDataOnly = { questions: partialResponse.questions[contentKey] };
        } else if (partialResponse.questions !== undefined) { 
            updatedSlideDataOnly = { questions: partialResponse.questions };
        }
    } else if (dimensionName === 'Connect') {
        const c = partialResponse.connections || {};
        const interconnectedness = firstNonEmptyString(
          c.interconnectedness,
          c.interconnected,
          partialResponse.interconnectedness
        );
        const completeDependency = firstNonEmptyString(
          c.completeDependency,
          c.complete_dependency,
          partialResponse.completeDependency,
          partialResponse.complete_dependency
        );
        const interdependency = firstNonEmptyString(
          c.interdependency,
          c.interdependence,
          partialResponse.interdependency,
          partialResponse.interdependence
        );
        const normalizedAllahNames = normalizeAllahNamesPayload(partialResponse);

        if (contentKey === 'allahNames' && (
            normalizedAllahNames.namesInEnglish.length > 0 ||
            normalizedAllahNames.namesInArabic.length > 0 ||
            normalizedAllahNames.whatItTells.length > 0
        )) {
            updatedSlideDataOnly = { allahNames: normalizedAllahNames };
        } else if (
          (contentKey === 'interdependency' || contentKey === 'completeDependency') &&
          (interdependency || completeDependency)
        ) {
            const value = interdependency || completeDependency;
            updatedSlideDataOnly = { interdependency: value, completeDependency: value };
        } else if (contentKey === 'interconnectedness' && interconnectedness) {
            updatedSlideDataOnly = { interconnectedness };
        } else if (contentKey === 'analogicalReflection' && partialResponse.analogicalReflection !== undefined) {
            updatedSlideDataOnly = { analogicalReflection: partialResponse.analogicalReflection };
        } else if (contentKey === 'questionsForDeeperConnection' && partialResponse.questionsForDeeperConnection !== undefined) {
            updatedSlideDataOnly = { questionsForDeeperConnection: partialResponse.questionsForDeeperConnection };
        } else if (contentKey === 'contemplationAndAppreciation' && partialResponse.contemplationAndAppreciation !== undefined) {
            updatedSlideDataOnly = { contemplationAndAppreciation: partialResponse.contemplationAndAppreciation };
        } else if (partialResponse[contentKey] !== undefined) { 
            updatedSlideDataOnly = { [contentKey]: partialResponse[contentKey] };
        }
    } else if (dimensionName === 'Appreciate') {
        if (contentKey === 'whatIfs' && partialResponse.whatIfs !== undefined) {
            updatedSlideDataOnly = { whatIfs: partialResponse.whatIfs };
        } else if (contentKey === 'characterLessons' && partialResponse.characterLessons !== undefined) {
            updatedSlideDataOnly = { characterLessons: partialResponse.characterLessons };
        } else if (contentKey === 'dua' && partialResponse.dua !== undefined) {
            updatedSlideDataOnly = { dua: partialResponse.dua };
        } else if (contentKey === 'zikrFikrShukr' && partialResponse.zikrFikrShukr) {
            updatedSlideDataOnly = { zikrFikrShukr: partialResponse.zikrFikrShukr };
        } else if (contentKey === 'connectWithQuran' && Array.isArray(partialResponse)) {
            // Handle case where AI returns the array directly instead of wrapped in an object
            updatedSlideDataOnly = { connectWithQuran: partialResponse };
        } else if (contentKey === 'connectWithQuran' && partialResponse.connectWithQuran) {
            updatedSlideDataOnly = { connectWithQuran: partialResponse.connectWithQuran };
        } else if (contentKey === 'connectWithHadith' && Array.isArray(partialResponse)) {
            // Handle case where AI returns the array directly instead of wrapped in an object
            updatedSlideDataOnly = { connectWithHadith: partialResponse };
        } else if (contentKey === 'connectWithHadith' && partialResponse.connectWithHadith) {
            updatedSlideDataOnly = { connectWithHadith: partialResponse.connectWithHadith };
        } else if (partialResponse[contentKey] !== undefined) { 
             updatedSlideDataOnly = { [contentKey]: partialResponse[contentKey] };
        }
    } else if (dimensionName === 'Activities' && partialResponse.activities) {
        updatedSlideDataOnly = { 
            activities: partialResponse.activities.map(activity => ({
                title: activity.title, dimension: activity.dimension, objective: activity.objective,
                materials: activity.materials || [], instructions: activity.instructions || [],
            }))
        };
    } else {
        console.warn("Unhandled case or unexpected partialResponse structure in createSlidesFromResponseForReset:", dimensionName, contentKey, partialResponse);
        if (typeof partialResponse === 'object' && partialResponse !== null && Object.keys(partialResponse).length > 0) {
            updatedSlideDataOnly = { ...partialResponse }; 
        } else {
            return []; 
        }
    }
    
    if (Object.keys(updatedSlideDataOnly).length === 0) {
        console.error("Failed to extract any updatable data from partialResponse for reset:", partialResponse, "Original Slide:", originalSlide);
        return []; 
    }

    const newContentForSlide = {
        ...updatedSlideDataOnly 
    };
    console.log("Content to merge by createSlidesFromResponseForReset:", newContentForSlide);
    return [newContentForSlide]; 
  };
   
  const handleBetaToggle = (e) => {
    setIsBetaTest(e.target.checked);
  };


  const handleFeedbackSubmit = async (feedbackData) => {
    try {
      await setDoc(doc(db, '5DThinkingLessonPlanFeedback', new Date().toISOString()), feedbackData);
      console.log('Feedback saved successfully:', feedbackData);
    } catch (error) {
      console.error('Error saving feedback:', error);
    }
  };
  


  
  const fetchUserData = async () => {
    if (!user) return;
  
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      // Create the user document if it doesn't exist
      await setDoc(doc(db, 'users', user.uid), {
        usageCount: 0,
        threads: [],
      });
    }
  };

  // Function to handle dimension selection
  const handleDimensionSelect = (dimension) => {
    setSelectedDimension(dimension);
  };

  // Function to toggle dimension selection for generation
  const handleDimensionToggle = (dimension) => {
    setSelectedDimensions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dimension)) {
        newSet.delete(dimension);
      } else {
        newSet.add(dimension);
      }
      return newSet;
    });
  };

  // Handlers for input changes
  const handleTopicChange = (e) => setTopic(e.target.value);
  const handleGradeLevelChange = (e) => setGradeLevel(e.target.value);

  // Handlers for each dimension input

  const handleObjectivesInputChange = (e) => setObjectivesInput(e.target.value);
  const handleExploreInputChange = (e) => setExploreInput(e.target.value);
  const handleCompareInputChange = (e) => setCompareInput(e.target.value);
  const handleQuestionInputChange = (e) => setQuestionInput(e.target.value);
  const handleConnectInputChange = (e) => setConnectInput(e.target.value);
  const handleAppreciateInputChange = (e) => setAppreciateInput(e.target.value);
  const handleActivitiesInputChange = (e) => setActivitiesInput(e.target.value);


  const handleStandardsChange = (e) => setStandards(e.target.value);

  const handleFileChange = (e, setFile, setPreview = null) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['text/plain', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.txt') && !file.name.toLowerCase().endsWith('.doc') && !file.name.toLowerCase().endsWith('.docx') && !file.name.toLowerCase().endsWith('.pdf')) {
        handleError(new Error('Invalid file type. Please upload a .txt, .doc, .docx, or .pdf file.'), 'fileReading');
        return;
      }

      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        handleError(new Error('File too large. Please upload a file smaller than 10MB.'), 'fileReading');
        return;
      }

      setFile(file);
      if (setPreview) {
        readFileContent(file)
          .then((content) => {
            setPreview(content);
          })
          .catch((error) => {
            handleError(new Error(`Failed to read file: ${error.message}`), 'fileReading');
            setPreview('');
            setFile(null);
          });
      }
    }
  };
  
  const handleFileDrop = (e, setFile, setPreview = null) => {
    e.preventDefault();
    
    try {
      const file = e.dataTransfer.files[0];
      if (file) {
        // Validate file type
        const allowedTypes = ['text/plain', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.txt') && !file.name.toLowerCase().endsWith('.doc') && !file.name.toLowerCase().endsWith('.docx') && !file.name.toLowerCase().endsWith('.pdf')) {
          handleError(new Error('Invalid file type. Please upload a .txt, .doc, .docx, or .pdf file.'), 'fileReading');
          return;
        }

        // Validate file size (10MB limit)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          handleError(new Error('File too large. Please upload a file smaller than 10MB.'), 'fileReading');
          return;
        }

        setFile(file);
        if (setPreview) {
          readFileContent(file)
            .then((content) => {
              setPreview(content);
            })
            .catch((error) => {
              handleError(new Error(`Failed to read dropped file: ${error.message}`), 'fileReading');
              setPreview('');
              setFile(null);
            });
        }
      }
    } catch (error) {
      handleError(new Error('Failed to process dropped file.'), 'fileReading');
    }
  };

  const handleError = (error, context = 'general') => {
    console.error('Error occurred:', error, 'Context:', context);
    
    let userMessage = '';
    
    switch (context) {
      case 'network':
        userMessage = 'Network connection issue. Please check your internet connection and try again.';
        break;
      case 'authentication':
        userMessage = 'Authentication failed. Please log in again and retry.';
        break;
      case 'fileReading':
        userMessage = 'Unable to read the uploaded file. Please ensure the file is not corrupted and try uploading again.';
        break;
      case 'aiProcessing':
        userMessage = 'AI processing failed. The service might be temporarily unavailable. Please try again in a few moments.';
        break;
      case 'slideGeneration':
        userMessage = 'Failed to generate slides. Please check your inputs and try again.';
        break;
      case 'reset':
        userMessage = 'Failed to reset the section. Please try again or regenerate the entire lesson plan.';
        break;
      case 'export':
        userMessage = 'Export failed. Please ensure you\'re logged in with proper permissions and try again.';
        break;
      case 'invalidInput':
        userMessage = 'Invalid input detected. Please check your topic content and try again.';
        break;
      case 'rateLimited':
        userMessage = 'Too many requests. Please wait a moment before trying again.';
        break;
      default:
        if (error?.message) {
          // Check for specific error types in the message
          if (error.message.includes('network') || error.message.includes('fetch')) {
            userMessage = 'Network error occurred. Please check your connection and try again.';
          } else if (error.message.includes('unauthorized') || error.message.includes('403')) {
            userMessage = 'Access denied. Please log in again and try again.';
          } else if (error.message.includes('quota') || error.message.includes('limit')) {
            userMessage = 'Service limit reached. Please try again later.';
          } else if (error.message.includes('timeout')) {
            userMessage = 'Request timed out. Please try again.';
          } else {
            userMessage = `An error occurred: ${error.message}. Please try again.`;
          }
        } else {
          userMessage = 'An unexpected error occurred. Please try again.';
        }
    }
    
    setError(userMessage);
    setIsLoading(false);
    setCurrentlyResettingKey(null);
    setIsExporting(false);
  };

  const handleSubmit = async () => {
    try {
      // Get topicContent
      let topicContent = topic;
      if (topicFile) {
        try {
          topicContent = await readFileContent(topicFile);
        } catch (fileError) {
          handleError(fileError, 'fileReading');
          return;
        }
      }

      if (!topicContent.trim()) {
        handleError(new Error('Topic content is required'), 'invalidInput');
        return;
      }

      // Get standardsContent
      let standardsContent = standards;
      if (standardsFile) {
        try {
          standardsContent = await readFileContent(standardsFile);
        } catch (fileError) {
          handleError(fileError, 'fileReading');
          return;
        }
      }

      // Prepare dimension inputs for selected dimensions only
      const dimensionInputs = {};
      const dimensionsList = Array.from(selectedDimensions);
      
      if (dimensionsList.length === 0) {
        handleError(new Error('Please select at least one dimension to generate'), 'invalidInput');
        return;
      }
      
      try {
        for (const dimension of dimensionsList) {
          let inputText = '';
          let inputFile = null;

          switch (dimension) {
            case 'Activities':
              inputText = activitiesInput;
              inputFile = activitiesFile;
              break;
            case 'Objectives':
              inputText = objectivesInput;
              inputFile = objectivesFile;
              break;
            case 'Explore':
              inputText = exploreInput;
              inputFile = exploreFile;
              break;
            case 'Compare':
              inputText = compareInput;
              inputFile = compareFile;
              break;
            case 'Question':
              inputText = questionInput;
              inputFile = questionFile;
              break;
            case 'Connect':
              inputText = connectInput;
              inputFile = connectFile;
              break;
            case 'Appreciate':
              inputText = appreciateInput;
              inputFile = appreciateFile;
              break;
            default:
              break;
          }

          let inputContent = inputText;
          if (inputFile) {
            try {
              const fileContent = await readFileContent(inputFile);
              inputContent += '\n' + fileContent;
            } catch (fileError) {
              handleError(new Error(`Failed to read ${dimension} file`), 'fileReading');
              return;
            }
          }

          dimensionInputs[dimension] = inputContent;
        }
      } catch (processingError) {
        handleError(processingError, 'slideGeneration');
        return;
      }

      setIsLoading(true);
      setError('');
      setSlides([]);
      setClaudeMessages([]);

      const processedDimensions = DIMENSIONS_CONFIG_BASE
        .filter(dimConfig => selectedDimensions.has(dimConfig.name))
        .map(dimConfig => ({
          ...dimConfig,
          input: dimensionInputs[dimConfig.name] || 'N/A'
        }));

      try {
        const assistantTitle = isBetaTest ? '5D Thinking-2' : '5D Thinking-1';
        setAssistantModel(assistantTitle);

        if (isBetaTest) {
          let history = [];
          for (const dimension of processedDimensions) {
            history = await fetchContentForDimensionClaude(
              history,
              dimension,
              topicContent,
              gradeLevel,
              standardsContent,
              false
            );
          }
          setClaudeMessages(history);
        } else {
          let latestResponseId = null;
          for (const dimension of processedDimensions) {
            latestResponseId = await fetchContentForDimension(
              latestResponseId,
              dimension,
              topicContent,
              gradeLevel,
              standardsContent,
              assistantTitle,
              false
            );
          }
          setCurrentResponseId(latestResponseId);

          // Store the latest response id and usage count in Firebase
          if (user && latestResponseId) {
            try {
              const userRef = doc(db, 'users', user.uid);
              await updateDoc(userRef, {
                '5DThinking.responses': arrayUnion(latestResponseId),
                '5DThinking.usageCount': increment(1),
              });
            } catch (dbError) {
              console.warn('Failed to update user data in database:', dbError);
            }
          }
        }
      } catch (aiError) {
        if (aiError.message?.includes('quota') || aiError.message?.includes('limit')) {
          handleError(aiError, 'rateLimited');
        } else if (aiError.message?.includes('network') || aiError.message?.includes('fetch')) {
          handleError(aiError, 'network');
        } else if (aiError.message?.includes('unauthorized')) {
          handleError(aiError, 'authentication');
        } else {
          handleError(aiError, 'aiProcessing');
        }
      } finally {
        setIsLoading(false);
        setCurrentlyResettingKey(null);
      }
    } catch (error) {
      handleError(error, 'slideGeneration');
    }
  };

  const updateSlidesForDimension = (response, dimensionName) => {
    const newSlidesForDimension = createSlidesFromResponse(response, dimensionName);
    setSlides((prevSlides) => {
      const slidesOfOtherDimensions = prevSlides.filter((s) => s.dimension !== dimensionName);
      const resultSlides = [];
      const dimensionsOrder = DIMENSIONS_CONFIG_BASE.map((d) => d.name);

      for (const orderedDimName of dimensionsOrder) {
        if (orderedDimName === dimensionName) {
          resultSlides.push(...newSlidesForDimension);
        } else {
          const existingOtherDimSlides = slidesOfOtherDimensions.filter(
            (s) => s.dimension === orderedDimName
          );
          if (existingOtherDimSlides.length > 0) {
            resultSlides.push(...existingOtherDimSlides);
          }
        }
      }

      if (!resultSlides.some((s) => s.dimension === dimensionName) && newSlidesForDimension.length > 0) {
        let inserted = false;
        const finalSlides = [];
        for (const orderedDimName of dimensionsOrder) {
          if (orderedDimName === dimensionName) {
            finalSlides.push(...newSlidesForDimension);
            inserted = true;
          } else {
            finalSlides.push(...prevSlides.filter((s) => s.dimension === orderedDimName));
          }
        }
        if (!inserted) finalSlides.push(...newSlidesForDimension);

        return finalSlides.filter(
          (slide, index, self) => index === self.findIndex((t) => t.id === slide.id)
        );
      }

      return resultSlides.filter(
        (slide, index, self) => index === self.findIndex((t) => t.id === slide.id)
      );
    });
  };

  const updateSingleResetSlide = (response, slideIdBeingReset) => {
    const originalSlide = slides.find((s) => s.id === slideIdBeingReset);
    if (!originalSlide) {
      throw new Error(`Original slide not found for reset: ${slideIdBeingReset}`);
    }

    const newSingleSlideArray = createSlidesFromResponseForReset(response, originalSlide);
    if (!newSingleSlideArray || newSingleSlideArray.length === 0) {
      throw new Error(`Failed to update slide ${slideIdBeingReset} with new content`);
    }

    const newSlide = newSingleSlideArray[0];
    setSlides((prevSlides) =>
      prevSlides.map((s) =>
        s.id === slideIdBeingReset
          ? {
              ...s,
              ...newSlide,
              id: s.id,
              dimension: s.dimension,
              contentKey: s.contentKey,
              subtopicIndex: s.subtopicIndex,
            }
          : s
      )
    );
  };

  const fetchContentForDimension = async (
    previousResponseId,
    dimension,
    topicContent,
    gradeLevel,
    standardsContent,
    assistantTitle,
    isResetting = false,
    slideIdBeingReset = null,
    customPrompt = null
  ) => {
    const dimensionInput = dimension.input || 'N/A';

    const basePrompt = customPrompt
      ? `TOPIC: ${topicContent}${gradeLevel ? `\nGRADE LEVEL: ${gradeLevel}` : ''}${standardsContent ? `\nSTANDARDS: ${standardsContent}` : ''}\nDIMENSION: ${dimension.name}\nDIMENSION-SPECIFIC INPUT: ${dimensionInput}\n\n${customPrompt}`
      : `TOPIC: ${topicContent}${gradeLevel ? `\nGRADE LEVEL: ${gradeLevel}` : ''}${standardsContent ? `\nSTANDARDS: ${standardsContent}` : ''}\nDIMENSION: ${dimension.name}\nDIMENSION-SPECIFIC INPUT: ${dimensionInput}`;

    const jsonEnforcedPrompt = `${basePrompt}\nReturn valid JSON only. Do not include any text outside JSON.`;

    console.log(
      `Fetching content via Responses API. Dimension: ${dimension.name}${isResetting ? `, Resetting SlideID: ${slideIdBeingReset}` : ''}.`
    );

    const model = FIVE_D_MODEL_BY_ASSISTANT[assistantTitle] || DEFAULT_FIVE_D_MODEL;
    const response = await createResponse(
      {
        model,
        store: true,
        instructions: FIVE_D_SYSTEM_PROMPT,
        input: jsonEnforcedPrompt,
        text: {
          format: {
            type: 'json_object',
          },
        },
        ...(previousResponseId ? { previous_response_id: previousResponseId } : {}),
      },
      assistantTitle
    );

    const responseText = extractResponseText(response);
    const parsedResponse = parseJsonPayload(responseText);

    if (isResetting && slideIdBeingReset) {
      updateSingleResetSlide(parsedResponse, slideIdBeingReset);
    } else {
      updateSlidesForDimension(parsedResponse, dimension.name);
    }

    const nextResponseId = response?.id || previousResponseId || null;
    setCurrentResponseId(nextResponseId);
    return nextResponseId;
  };
  
  const fetchContentForDimensionClaude = async (
    previousMessages,
    dimension,
    topicContent,
    gradeLevel,
    standardsContent,
    isResetting = false,
    slideIdBeingReset = null,
    customPrompt = null
  ) => {
    const dimensionInput = dimension.input || 'N/A';

    const basePrompt = customPrompt
      ? `TOPIC: ${topicContent}${gradeLevel ? `\nGRADE LEVEL: ${gradeLevel}` : ''}${standardsContent ? `\nSTANDARDS: ${standardsContent}` : ''}\nDIMENSION: ${dimension.name}\nDIMENSION-SPECIFIC INPUT: ${dimensionInput}\n\n${customPrompt}`
      : `TOPIC: ${topicContent}${gradeLevel ? `\nGRADE LEVEL: ${gradeLevel}` : ''}${standardsContent ? `\nSTANDARDS: ${standardsContent}` : ''}\nDIMENSION: ${dimension.name}\nDIMENSION-SPECIFIC INPUT: ${dimensionInput}`;

    const userMessage = `${basePrompt}\nReturn valid JSON only. Do not include any text outside JSON.`;

    const messages = [...previousMessages, { role: 'user', content: userMessage }];

    const response = await createClaudeResponse({
      model: CLAUDE_MODEL,
      system: FIVE_D_SYSTEM_PROMPT,
      messages,
      max_tokens: 8096,
    });

    const responseText = response.content?.[0]?.text || '';
    const parsedResponse = parseJsonPayload(responseText);

    if (isResetting && slideIdBeingReset) {
      updateSingleResetSlide(parsedResponse, slideIdBeingReset);
    } else {
      updateSlidesForDimension(parsedResponse, dimension.name);
    }

    return [...messages, { role: 'assistant', content: responseText }];
  };

  const handleResetDimension = async (slideIdToReset) => {
    try {
      if (!currentResponseId) {
        handleError(new Error("No active session found. Please generate slides first."), 'reset');
        return;
      }

      const parts = slideIdToReset.split('-');
      const dimensionName = parts[0];

      const originalSlide = slides.find(s => s.id === slideIdToReset);
      if (!originalSlide) {
        handleError(new Error(`Could not find original slide data for ID: ${slideIdToReset}`), 'reset');
        return;
      }

      const actualContentKey = originalSlide.contentKey;

      const subtopicTitleMap = {
        'Objectives-learningObjectives': 'Learning Objectives',
        'Explore-content': 'Main Content',
        'Explore-explanation': 'Explanation',
        'Explore-observations': 'Observations',
        'Explore-fascinatingFacts': 'Fascinating Facts',
        'Compare-analogy': 'Analogy',
        'Compare-content': 'Comparison Content',
        'Compare-explanation': 'Comparison Explanation',
        'Compare-comparison': 'Comparison Table/Details',
        'Question-negation_of_chance': 'Questions on Negation of Chance',
        'Question-negation_of_material_causes': 'Questions on Negation of Material Causes',
        'Question-negation_of_nature': 'Questions on Negation of Nature',
        'Question-conclusion': 'Conclusion for Question Dimension',
        'Connect-interconnectedness': 'Interconnectedness Section',
        'Connect-interdependency': 'Interdependency Section',
        'Connect-completeDependency': 'Complete Dependency Section',
        'Connect-allahNames': "Allah's Names Section",
        'Connect-analogicalReflection': 'Analogical Reflection',
        'Connect-questionsForDeeperConnection': 'Questions for Deeper Connection',
        'Connect-contemplationAndAppreciation': 'Contemplation and Appreciation',
        'Appreciate-whatIfs': "'What Ifs' Section",
        'Appreciate-zikrFikrShukr': "'Zikr, Fikr, Shukr' Section",
        'Appreciate-characterLessons': 'Character Lessons',
        'Appreciate-connectWithQuran': 'Connection with Quran',
        'Appreciate-connectWithHadith': 'Connection with Hadith',
        'Appreciate-dua': 'Dua (Supplication) Section',
        'Activities-main': 'Main Activities List'
      };

      const subtopicTitleForPrompt = subtopicTitleMap[`${dimensionName}-${actualContentKey}`] || actualContentKey.replace(/_/g, ' ') || 'the specific section';

      setCurrentlyResettingKey(slideIdToReset); 
      setIsLoading(true); 
      setError('');

      let topicContentForReset = topic;
      if (topicFile) { 
        try { 
          topicContentForReset = await readFileContent(topicFile); 
        } catch(e) { 
          handleError(e, 'fileReading'); 
          return; 
        }
      }

      if (!topicContentForReset.trim() && !topicFile) {
        console.warn("Topic content for reset is based on current input.");
      }

      let standardsContentForReset = standards;
      if (standardsFile) { 
        try { 
          standardsContentForReset = await readFileContent(standardsFile); 
        } catch(e) { 
          handleError(e, 'fileReading'); 
          return; 
        }
      }

      const getOriginalDimensionInput = async (dimName) => {
        try {
          let inputText = ''; let inputFile = null;
          switch (dimName) {
            case 'Objectives': inputText = objectivesInput; inputFile = objectivesFile; break;
            case 'Explore': inputText = exploreInput; inputFile = exploreFile; break;
            case 'Compare': inputText = compareInput; inputFile = compareFile; break;
            case 'Question': inputText = questionInput; inputFile = questionFile; break;
            case 'Connect': inputText = connectInput; inputFile = connectFile; break;
            case 'Appreciate': inputText = appreciateInput; inputFile = appreciateFile; break;
            case 'Activities': inputText = activitiesInput; inputFile = activitiesFile; break;
            default: return 'N/A';
          }
          let content = inputText;
          if (inputFile) { 
            try { 
              const fileContent = await readFileContent(inputFile); 
              content = content ? `${content}\n${fileContent}` : fileContent; 
            } catch (e) { 
              handleError(new Error(`Error reading file for ${dimName}: ${e.message}`), 'fileReading'); 
              throw e; 
            }
          }
          return content || 'N/A';
        } catch (error) {
          handleError(error, 'fileReading');
          throw error;
        }
      };
      
      let originalDimensionOverallInput;
      try { 
        originalDimensionOverallInput = await getOriginalDimensionInput(dimensionName); 
      } catch (e) { 
        return; 
      }

      const dimensionConfig = DIMENSIONS_CONFIG_BASE.find(d => d.name === dimensionName);
      if (!dimensionConfig) { 
        handleError(new Error(`Configuration for ${dimensionName} not found.`), 'reset'); 
        return; 
      }

      const resetPrompt = `Reset and return a new different "${subtopicTitleForPrompt}" section only.`;

      const assistantTitle = isBetaTest ? '5D Thinking-2' : '5D Thinking-1';
      const dimObj = { name: dimensionName, title: dimensionConfig.title, input: originalDimensionOverallInput };

      try {
        if (isBetaTest) {
          await fetchContentForDimensionClaude(
            claudeMessages,
            dimObj,
            topicContentForReset, gradeLevel, standardsContentForReset,
            true, slideIdToReset, resetPrompt
          );
        } else {
          await fetchContentForDimension(
            currentResponseId,
            dimObj,
            topicContentForReset, gradeLevel, standardsContentForReset, assistantTitle,
            true, slideIdToReset, resetPrompt
          );
        }
      } catch (error) {
        if (error.message?.includes('quota') || error.message?.includes('limit')) {
          handleError(error, 'rateLimited');
        } else if (error.message?.includes('network')) {
          handleError(error, 'network');
        } else {
          handleError(error, 'reset');
        }
      } finally {
        setIsLoading(false);
        setCurrentlyResettingKey(null);
      }
    } catch (error) {
      handleError(error, 'reset');
    }
  };

  // Main function to export slides
  const exportSlidesToGoogleSlides = async () => {
    console.log('DEBUG: Exporting slides to Google Slides:', slides);
    if (!slides.length) {
      handleError(new Error('Please generate slides first.'), 'export');
      return;
    }
  
    setIsLoading(true);
    setError('');
  
    try {
      const accessToken = sessionStorage.getItem('googleAuthToken');
      const tokenExpiryTime = sessionStorage.getItem('tokenExpiryTime');
  
      // Check if access token is missing or expired
      if (!accessToken || (tokenExpiryTime && new Date() > new Date(tokenExpiryTime))) {
        handleError(new Error('Access token is invalid or expired. Please authenticate again.'), 'authentication');
        setIsLoginModalOpen(true);
        return;
      }
  
      const maxRetries = 3;
      let attempt = 0;
      let success = false;
      
      while (attempt < maxRetries && !success) {
        try {
          attempt++;
          setIsExporting(true);
          const newPresentationId = await createPresentation(accessToken, 'New 5D Lesson Plan');
          await updatePresentation(newPresentationId, accessToken, slides);
          const presentationLink = `https://docs.google.com/presentation/d/${newPresentationId}/edit`;
          setPresentationLink(presentationLink);
          alert('Google Slides presentation created successfully!');
          success = true;
          setIsExporting(false);
        } catch (error) {
          console.error(`Attempt ${attempt} failed:`, error);
          if (attempt >= maxRetries) {
            if (error.message?.includes('quota') || error.message?.includes('limit')) {
              handleError(error, 'rateLimited');
            } else if (error.message?.includes('network')) {
              handleError(error, 'network');
            } else if (error.message?.includes('403') || error.message?.includes('unauthorized')) {
              handleError(error, 'authentication');
              setIsLoginModalOpen(true);
            } else {
              handleError(new Error('Failed to create Google Slides presentation after multiple attempts.'), 'export');
            }
          }
        }
      }
    } catch (error) {
      console.error('Error during Google Slides export:', error);
      if (error.message?.includes('authentication') || error.message?.includes('unauthorized')) {
        handleError(error, 'authentication');
        setIsLoginModalOpen(true);
      } else {
        handleError(error, 'export');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Function to render the input for the selected dimension
  const renderDimensionInput = () => {
    let inputValue = '';
    let onChangeHandler = () => {};
    let fileValue = null;
    let setFileHandler = () => {};

    switch (selectedDimension) {
      case 'Objectives':
        inputValue = objectivesInput;
        onChangeHandler = handleObjectivesInputChange;
        fileValue = objectivesFile;
        setFileHandler = setObjectivesFile;
        break;
      case 'Explore':
        inputValue = exploreInput;
        onChangeHandler = handleExploreInputChange;
        fileValue = exploreFile;
        setFileHandler = setExploreFile;
        break;
      case 'Compare':
        inputValue = compareInput;
        onChangeHandler = handleCompareInputChange;
        fileValue = compareFile;
        setFileHandler = setCompareFile;
        break;
      case 'Question':
        inputValue = questionInput;
        onChangeHandler = handleQuestionInputChange;
        fileValue = questionFile;
        setFileHandler = setQuestionFile;
        break;
      case 'Connect':
        inputValue = connectInput;
        onChangeHandler = handleConnectInputChange;
        fileValue = connectFile;
        setFileHandler = setConnectFile;
        break;
      case 'Appreciate':
        inputValue = appreciateInput;
        onChangeHandler = handleAppreciateInputChange;
        fileValue = appreciateFile;
        setFileHandler = setAppreciateFile;
        break;
      case 'Activities':
        inputValue = activitiesInput;
        onChangeHandler = handleActivitiesInputChange;
        fileValue = activitiesFile;
        setFileHandler = setActivitiesFile;
        break;
      default:
        break;
    }

    return (
      <div className="input-group">
        <h3> <FaBook/> {selectedDimension} Input (Optional):</h3>
        <div className="input-container">
          <textarea
            value={inputValue}
            onChange={onChangeHandler}
            placeholder={`Provide specific input for the '${selectedDimension}' dimension`}
            rows={4}
          />
          <label htmlFor={`${selectedDimension}FileInput`} className="file-upload-icon">
            <FaPaperclip />
          </label>
          <input
            type="file"
            id={`${selectedDimension}FileInput`}
            onChange={(e) => handleFileChange(e, setFileHandler)}
            accept=".txt,.doc,.docx,.pdf"
            style={{ display: 'none' }}
          />
        </div>
        {/* Display the uploaded file name if a file is selected */}
        {fileValue && (
          <div className="file-name">
            <p>Uploaded file: {fileValue.name}</p>
          </div>
        )}
      </div>
    );
  };
  
  const handleExportToPowerPoint = () => {
    if (slides.length === 0) {
      alert('No slides available to export.');
      return;
    }

    console.log('DEBUG: Exporting slides to PowerPoint:', slides);
    try {
      exportSlidesAsPptx(slides);
      alert('PowerPoint export successful!');
    } catch (error) {
      console.error('Error exporting to PowerPoint:', error);
      alert('Failed to export to PowerPoint. Please try again.');
    } finally {
      setIsExportDropdownOpen(false);
    }
  };

  return (
    <div className="five-d-assistant-container">
      {isLoginModalOpen && (
        <LoginModal
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          onLogin={() => {
            console.log('Login successful, closing modal');
            setIsLoginModalOpen(false);
            setError('');
          }}
        />
      )}
     {/* Add Feedback Modal */}
     <FeedbackModal
  isOpen={isFeedbackModalOpen}
  onClose={() => setIsFeedbackModalOpen(false)}
  assistantModel={assistantModel}
  collectedResponses={collectedResponses}
  onSubmit={handleFeedbackSubmit}
/>


      <header className="five-d-assistant-header">
        <h1 className="five-d-title"> 5D Lesson Planner </h1> 
        <img src={fiveDThinkingImg} alt="5D Lesson Planner" className="five-d-logo" />    
        
        {/* Add feedback button to header */}
        <button 
  className="feedback-button"
  onClick={() => setIsFeedbackModalOpen(true)}
>
  <FaComments style={{ marginRight: '8px' }} />
  Feedback
</button>

      </header>

      <div className="input-section">
        <div className="input-group">
            {/* Beta Toggle in top-right corner */}
    <BetaToggle isBetaTest={isBetaTest} setIsBetaTest={setIsBetaTest} />
          <h3>
            <FaListAlt /> Topic Content (Required):
          </h3>
          <div
            className="input-container"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleFileDrop(e, setTopicFile, setTopicPreview)}
          >
            <input
              type="text"
              value={topic}
              onChange={handleTopicChange}
              placeholder="Enter the main topic here"
            />
            <label htmlFor="topicFileInput" className="file-upload-icon">
              <FaPaperclip />
            </label>
            <input
              type="file"
              id="topicFileInput"
              onChange={(e) => handleFileChange(e, setTopicFile, setTopicPreview)}
              accept=".txt,.doc,.docx,.pdf"
              style={{ display: 'none' }}
            />
          </div>
          {topicPreview && (
            <div className="file-preview">
              <h4>Topic Preview:</h4>
              <p>
                {topicPreview.slice(0, 500)}
                {topicPreview.length > 500 && '...'}
              </p>
            </div>
          )}
        </div>

        {/* Grade Level Input (Optional) */}
        <div className="input-group">
          <h3> <FaGraduationCap/> Grade Level (Optional):</h3>
          <input
            type="text"
            value={gradeLevel}
            onChange={handleGradeLevelChange}
            placeholder="Specify the grade level (e.g., 9th Grade, Middle School)"
          />
        </div>

        {/* Standards Input (Optional) */}
        <div className="input-group">
          <h3>
            <FaExclamationCircle /> Curriculum Expectations/Standards (Optional):
          </h3>
          <div
            className="input-container"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleFileDrop(e, setStandardsFile, setStandardsPreview)}
          >
            <input
              type="text"
              value={standards}
              onChange={handleStandardsChange}
              placeholder="Enter curriculum expectations/standards to align with (e.g., CCSS, TEKS, NGSS, or your own curriculum standards)"
            />
            <label htmlFor="standardsFileInput" className="file-upload-icon">
              <FaPaperclip />
            </label>
            <input
              type="file"
              id="standardsFileInput"
              onChange={(e) => handleFileChange(e, setStandardsFile, setStandardsPreview)}
              accept=".txt,.doc,.docx,.pdf"
              style={{ display: 'none' }}
            />
          </div>
          {standardsPreview && (
            <div className="file-preview">
              <h4>Curriculum Expectations/Standards Preview:</h4>
              <p>
                {standardsPreview.slice(0, 500)}
                {standardsPreview.length > 500 && '...'}
              </p>
            </div>
          )}
        </div>


        {/* Dimension Selector - Tabbed Interface */}
        <div className="dimension-tabs-container">
        <div className="selection-badge" style={{ marginBottom: '10px' }}>
            {selectedDimensions.size === 7 ? (
              <span style={{ color: '#2a9d8f' }}>(All selected)</span>
            ) : selectedDimensions.size === 0 ? (
              <span style={{ color: '#e76f51' }}>(None selected)</span>
            ) : (
              <span>({selectedDimensions.size} selected)</span>
            )}
          </div>
          <h4>Dimensions to Generate:</h4>
          
          {/* Selection Badge - Moved to top */}
          
          
          <div className="dimension-tabs">
            {Object.keys(dimensionColors).map((dimension) => {
              const isSelected = selectedDimensions.has(dimension);
              const isActive = selectedDimension === dimension;
              
              return (
                <button
                  key={dimension}
                  className={`dimension-tab ${isActive ? 'active' : ''} ${isSelected ? 'selected' : 'unselected'}`}
                  onClick={() => handleDimensionSelect(dimension)}
                  style={{
                    backgroundColor: isActive 
                      ? `#${dimensionColors[dimension]}` 
                      : isSelected 
                        ? 'rgba(255, 255, 255, 0.2)'
                        : 'rgba(255, 255, 255, 0.05)',
                    borderColor: `#${dimensionColors[dimension]}`,
                    color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.8)',
                    borderWidth: isSelected ? '2px' : '1px',
                    borderStyle: isSelected ? 'solid' : 'dashed',
                    position: 'relative',
                    paddingRight: '35px'
                  }}
                  title={`Click to select for input | Click checkbox to ${isSelected ? 'exclude' : 'include'} from generation`}
                >
                  {dimension}
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleDimensionToggle(dimension)}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      width: '16px',
                      height: '16px'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </button>
              );
            })}
          </div>
          <p className="dimension-help-text">
            Click a tab to add input | Click checkbox to include/exclude from generation
          </p>
        </div>

        {/* Input for Selected Dimension */}
        {renderDimensionInput()}

        {/* Stacked Input Highlights - Only Visible When Content Exists */}
        {(() => {
          const allInputs = [
            objectivesInput,
            exploreInput,
            compareInput,
            questionInput,
            connectInput,
            appreciateInput,
            activitiesInput
          ];
          const hasAnyContent = allInputs.some(input => input.trim().length > 0);
          
          if (!hasAnyContent) return null;
          
          return (
            <div className="input-highlights-section">
              <h3>Your Custom Inputs ({selectedDimensions.size} dimensions selected for generation)</h3>
              <div className="input-highlights-container">
                {Object.keys(dimensionColors).map((dimension) => {
                  let inputValue = '';
                  switch (dimension) {
                    case 'Objectives':
                      inputValue = objectivesInput;
                      break;
                    case 'Explore':
                      inputValue = exploreInput;
                      break;
                    case 'Compare':
                      inputValue = compareInput;
                      break;
                    case 'Question':
                      inputValue = questionInput;
                      break;
                    case 'Connect':
                      inputValue = connectInput;
                      break;
                    case 'Appreciate':
                      inputValue = appreciateInput;
                      break;
                    case 'Activities':
                      inputValue = activitiesInput;
                      break;
                    default:
                      inputValue = '';
                  }
                  
                  const hasContent = inputValue.trim().length > 0;
                  const isSelected = selectedDimensions.has(dimension);
                  
                  // Only show dimensions that have content
                  if (!hasContent) return null;
                  
                  return (
                    <div
                      key={dimension}
                      className={`input-highlight-card has-content ${isSelected ? 'selected-for-generation' : 'not-selected'}`}
                      style={{
                        borderLeft: `4px solid #${dimensionColors[dimension]}`,
                        backgroundColor: `rgba(${parseInt(dimensionColors[dimension].substring(0, 2), 16)}, ${parseInt(dimensionColors[dimension].substring(2, 4), 16)}, ${parseInt(dimensionColors[dimension].substring(4, 6), 16)}, 0.1)`,
                        opacity: isSelected ? 1 : 0.5
                      }}
                    >
                      <div className="input-highlight-header">
                        <span 
                          className="dimension-badge"
                          style={{ 
                            backgroundColor: `#${dimensionColors[dimension]}`,
                            opacity: 1
                          }}
                        >
                          {dimension}
                        </span>
                        {!isSelected && (
                          <span className="not-selected-indicator">Not selected for generation</span>
                        )}
                      </div>
                      <div className="input-highlight-content">
                        {inputValue}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        <button onClick={handleSubmit} disabled={isLoading}>
  {isLoading ? (
    <>
      Generating Slides...
      <FaSpinner className="spinner-small" style={{ marginLeft: '8px' }} />
    </>
  ) : (
    'Generate Slides'
  )}
</button>

        {error && <p className="error-message-5d">{error}</p>}
      </div>

      {slides.length > 0 && (
        <div className="slide-content">
          <h2>Generated Slides</h2>
          {slides.map((slide, index) => (
            <SlideContent 
                key={slide.id || index} 
                slide={slide} 
                onResetDimension={handleResetDimension}
                isResettingThisSlide={currentlyResettingKey === slide.id} 
            />
          ))}

          {/* Export Buttons and Dropdown */}
    <div className="export-dropdown-container">
    <button onClick={toggleExportDropdown} className="export-button">
  <FaDownload style={{ marginRight: '8px' }} /> Export Slides {isExportDropdownOpen ? <FaChevronUp style={{ marginLeft: '8px' }} /> : <FaChevronDown style={{ marginLeft: '8px' }} />}
</button>

      {isExportDropdownOpen && (
        <div className="export-dropdown">
          <button className="export-button" onClick={handleExportToPowerPoint}>
            <FaFilePowerpoint style={{ marginRight: '8px' }} /> Export as PowerPoint
          </button>
          <button className="export-button" onClick={exportSlidesToGoogleSlides}>
            <FaGoogleDrive style={{ marginRight: '8px' }} /> Export to Google Slides
          </button>
          <button className="export-button" onClick={handleExportToGoogleDocs}>
            <FaGoogleDrive style={{ marginRight: '8px' }} /> Export to Google Docs
          </button>
          <button className="export-button" onClick={handleExportToWord}>
            <FaFileWord style={{ marginRight: '8px' }} /> Export to Word
          </button>
        </div>
      )}
    </div>

    {/* Spinner overlay */}
    {isExporting && (
      <div className="export-spinner-overlay">
        <FaSpinner className="spinner" />
      </div>
    )}


          {presentationLink && (
            <div className="presentation-link">
              <p>Your presentation is ready:</p>
              <a href={presentationLink} target="_blank" rel="noopener noreferrer">
                Open Presentation
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FiveDAssistant;
